import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import {
  CreateCompanyRequest,
  useCreateCompanyMutation,
} from "@/services/backend/company/api/company";
import { useState } from "react";
import { v4 } from "uuid";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { toast } from "sonner";

export function CompanyCreateDialog({
  onCreated = undefined,
}: {
  onCreated?: (companyId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(newRequest());
  const [create, { isLoading, error, reset }] = useCreateCompanyMutation();

  const doCreate = () => {
    if (isLoading) {
      return;
    }
    const { id } = request;
    create(request)
      .unwrap()
      .then(() => {
        toast.success(t("Unternehmen erstellt"));
        setRequest(newRequest());
        reset();
        setOpen(false);
        onCreated?.(id);
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={Plus} label={t("Unternehmen")} size="sm" />
      <DialogContent>
        <CommonDialogHeader
          title={t("Unternehmen erstellen")}
          description={t(
            "Ein Unternehmen ist ein Unternehmen der GESA Gruppe. Die weiteren Stammdaten werden auf der Seite des Unternehmens gepflegt.",
          )}
        />
        <Fields request={request} onRequestChange={setRequest} error={error} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          actionLabel={t("Erstellen")}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}

function Fields({
  request,
  onRequestChange,
  error,
}: {
  request: CreateCompanyRequest;
  onRequestChange: (request: CreateCompanyRequest) => void;
  error: unknown;
}) {
  return (
    <div className="grid gap-4">
      <TextInput
        label={t("Name")}
        field="name"
        text={request.name}
        onTextChange={(name) => onRequestChange({ ...request, name })}
        errors={inputValidationErrorMessages(error, "name")}
      />
      <TextInput
        label={t("Kurzform")}
        field="shortName"
        text={request.shortName}
        onTextChange={(shortName) => onRequestChange({ ...request, shortName })}
        errors={inputValidationErrorMessages(error, "shortName")}
      />
    </div>
  );
}

function newRequest(): CreateCompanyRequest {
  return {
    id: v4(),
    name: "",
    shortName: "",
  };
}
